/**
 * Total Pay consistency check for dentists on the CDP Reports page.
 *
 * Compares the Total Pay figure across the surfaces where it's shown/sent:
 *  - CDP Report — row.totalPay, already computed for the visible table (CdpReportsTableByDentists.js)
 *  - Sage Export — derived here via the exact same getSageReportCalculatedData() function the real
 *                  Sage export download uses, so the check never drifts from the actual export.
 *  - Sent (Earnings Report) — read from the pay_validation table via getEarningsReportTotals,
 *                  which captures the figure at send time with no recalculation.
 */
import { getSageReportCalculatedData } from '../../../utils/exportUtils';

const CURRENCY_TOLERANCE = 0.01;

export const round = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const computeCdpReportTotal = (row) => round(row?.totalPay);

export const computeSageExportTotal = (row, getDoctorFullName, startDate) => {
    if (!row) return 0;
    // Use the same function as the real Sage export download. Filter to doctor rows only —
    // getSageReportCalculatedData also returns account-2230 reverse entries (no doctor field)
    // which must not be included in the doctor's total.
    const sageRows = getSageReportCalculatedData(startDate, [row], getDoctorFullName, false) || [];
    return round(sageRows.filter(r => r.doctor).reduce((sum, r) => sum + (Number(r.debit) || 0), 0));
};

/**
 * Builds the { surfaceName: value } map for a dentist, skipping surfaces with no value
 * (e.g. Earnings Report never sent for this period) rather than treating them as zero.
 */
const buildValueMap = ({ cdpReportTotal, sageExportTotal, earningsReportTotal, earningsReportSent }) => {
    const values = {
        cdpReport: cdpReportTotal,
        sageExport: sageExportTotal
    };
    if (earningsReportSent) {
        values.earningsReport = earningsReportTotal;
    }
    return values;
};

export const buildDiscrepancies = (values) => {
    const surfaces = Object.entries(values).filter(([, value]) => value !== null && value !== undefined);
    const discrepancies = [];
    for (let i = 0; i < surfaces.length; i++) {
        for (let j = i + 1; j < surfaces.length; j++) {
            const [surfaceA, valueA] = surfaces[i];
            const [surfaceB, valueB] = surfaces[j];
            const diff = round(valueA - valueB);
            if (Math.abs(diff) >= CURRENCY_TOLERANCE) {
                discrepancies.push({ surfaceA, surfaceB, diff });
            }
        }
    }
    return discrepancies;
};

/**
 * Verifies Total Pay consistency for a single dentist row.
 *
 * @param {Object} params
 * @param {Object} params.row
 * @param {string} params.startDate
 * @param {Function} params.getDoctorFullName
 * @param {Function} params.fetchEarningsReportTotal - async (abbr, startDate) => { sent, totalEarnings, sentAt } | null
 * @param {Function} [params.onLog] - called with each request activity entry
 */
export const verifyDentistTotalPay = async ({ row, startDate, getDoctorFullName, fetchEarningsReportTotal, onLog }) => {
    const abbr = row?.ABBR;
    const doctorName = row?.doctorName || (getDoctorFullName ? getDoctorFullName(abbr) : abbr);

    const cdpReportTotal = computeCdpReportTotal(row);
    const sageExportTotal = computeSageExportTotal(row, getDoctorFullName, startDate);

    onLog?.({ abbr, doctorName, surface: 'earningsReport', status: 'pending', ts: Date.now() });

    let earningsResult = null;
    try {
        earningsResult = await fetchEarningsReportTotal(abbr, startDate);
        onLog?.({
            abbr, doctorName, surface: 'earningsReport',
            status: earningsResult?.sent ? 'ok' : 'not_sent',
            value: earningsResult?.sent ? round(earningsResult.totalEarnings) : null,
            sentAt: earningsResult?.sentAt || null,
            ts: Date.now()
        });
    } catch (err) {
        onLog?.({ abbr, doctorName, surface: 'earningsReport', status: 'error', error: err?.message, ts: Date.now() });
    }

    const earningsReportSent = !!(earningsResult && earningsResult.sent);
    const earningsReportTotal = earningsReportSent ? round(earningsResult.totalEarnings) : null;

    const values = buildValueMap({ cdpReportTotal, sageExportTotal, earningsReportTotal, earningsReportSent });
    const discrepancies = buildDiscrepancies(values);

    return {
        doctorId: abbr,
        doctorName,
        contractType: row?.contractType || null,
        startDate,
        values,
        earningsReportSent,
        earningsReportSentAt: earningsResult?.sentAt || null,
        match: discrepancies.length === 0,
        discrepancies
    };
};

/**
 * Verifies Total Pay consistency for every dentist row currently shown on the CDP Reports page.
 * Streams results via onProgress as each dentist completes.
 *
 * @param {Function} [params.onProgress] - called after each dentist: ({ result, completed, total })
 * @param {Function} [params.onLog]      - forwarded to verifyDentistTotalPay for request-level events
 */
export const verifyAllDentistsTotalPay = async ({ rows, startDate, getDoctorFullName, fetchEarningsReportTotal, onProgress, onLog, concurrency = 4 }) => {
    const total = (rows || []).length;
    let completed = 0;
    const queue = [...(rows || [])];
    const results = [];

    const workerCount = Math.max(1, Math.min(concurrency, queue.length));
    const workers = new Array(workerCount).fill(null).map(async () => {
        while (queue.length) {
            const row = queue.shift();
            let result;
            try {
                result = await verifyDentistTotalPay({ row, startDate, getDoctorFullName, fetchEarningsReportTotal, onLog });
            } catch (err) {
                result = {
                    doctorId: row?.ABBR,
                    doctorName: row?.doctorName || row?.ABBR,
                    contractType: row?.contractType || null,
                    startDate,
                    error: err.message,
                    match: false,
                    discrepancies: []
                };
            }
            results.push(result);
            completed++;
            onProgress?.({ result, completed, total });
        }
    });

    await Promise.all(workers);

    const orderByAbbr = new Map((rows || []).map((row, idx) => [row?.ABBR, idx]));
    return results.sort((a, b) => (orderByAbbr.get(a.doctorId) ?? 0) - (orderByAbbr.get(b.doctorId) ?? 0));
};
